/**
 * Central Contract Calls Hook
 *
 * Single source of truth for all smart contract interactions.
 * All calls are gasless via Alchemy.
 */

import { useState, useCallback } from 'react';
import { encodeFunctionData, parseUnits } from 'viem';
import { useGaslessSendTransaction } from './useAlchemy';
import { CONTRACTS } from '@/config/contracts';
import DasherFactoryABI from '@/abi/DasherFactory';
import DasherABI from '@/abi/Dasher';

// Types
interface CreateChallengeParams {
  startDate: number; // Unix timestamp
  duration: number; // Duration in seconds
  stakeAmount: string; // Amount in token units (e.g., "100" for 100 USDC)
}

interface JoinChallengeParams {
  challengeAddress: string;
  tokenAddress: string;
  stakeAmount: string;
}

interface ClaimRewardsParams {
  challengeAddress: string;
  proof: string[];
  index: number;
  amount: string;
}

interface TransferToWalletParams {
  recipientAddress: string;
  tokenAddress: string;
  amount: string;
}

export function useContractCalls() {
  const { sendTransaction, isLoading } = useGaslessSendTransaction();
  const [error, setError] = useState<Error | null>(null);

  /**
   * Create a new challenge (gasless)
   *
   * @example
   * const { createChallenge } = useContractCalls();
   * const result = await createChallenge({
   *   startDate: Math.floor(Date.now() / 1000),
   *   duration: 7 * 24 * 60 * 60, // 7 days in seconds
   *   stakeAmount: "10" // 10 USDC
   * });
   */
  const createChallenge = useCallback(
    async ({ startDate, duration, stakeAmount }: CreateChallengeParams) => {
      try {
        setError(null);

        // Convert stake amount to proper units (assuming 6 decimals for USDC)
        const stakeAmountWei = parseUnits(stakeAmount, 6);

        const data = encodeFunctionData({
          abi: DasherFactoryABI,
          functionName: 'createChallenge',
          args: [
            BigInt(startDate),
            BigInt(duration),
            stakeAmountWei,
          ],
        });

        const result = await sendTransaction({
          to: CONTRACTS.DASHER_FACTORY.address,
          data,
          value: '0x0',
        });

        return result;
      } catch (err: any) {
        console.error('[Contract] Create challenge failed:', err);
        setError(err);
        throw err;
      }
    },
    [sendTransaction]
  );

  /**
   * Join a challenge (gasless, batched: approve + join)
   *
   * @example
   * const { joinChallenge } = useContractCalls();
   * await joinChallenge({
   *   challengeAddress: '0x...',
   *   tokenAddress: '0x...',
   *   stakeAmount: "10"
   * });
   */
  const joinChallenge = useCallback(
    async ({ challengeAddress, tokenAddress, stakeAmount }: JoinChallengeParams) => {
      try {
        setError(null);

        // Convert stake amount to proper units
        const stakeAmountWei = parseUnits(stakeAmount, 6);

        // ERC20 ABI for approve function
        const ERC20_ABI = [
          {
            name: 'approve',
            type: 'function',
            stateMutability: 'nonpayable',
            inputs: [
              { name: 'spender', type: 'address' },
              { name: 'amount', type: 'uint256' },
            ],
            outputs: [{ name: '', type: 'bool' }],
          },
        ] as const;

        // Encode approve call
        const approveData = encodeFunctionData({
          abi: ERC20_ABI,
          functionName: 'approve',
          args: [challengeAddress as `0x${string}`, stakeAmountWei],
        });

        // Encode joinChallenge call
        const joinData = encodeFunctionData({
          abi: DasherABI,
          functionName: 'joinChallenge',
          args: [],
        });

        // Batch both transactions
        const result = await sendTransaction([
          { to: tokenAddress as `0x${string}`, data: approveData },
          { to: challengeAddress as `0x${string}`, data: joinData },
        ]);

        return result;
      } catch (err: any) {
        console.error('[Contract] Join challenge failed:', err);
        setError(err);
        throw err;
      }
    },
    [sendTransaction]
  );

  /**
   * Claim rewards from a completed challenge (gasless)
   *
   * @example
   * const { claimRewards } = useContractCalls();
   * await claimRewards({
   *   challengeAddress: '0x...',
   *   proof: ['0x...', '0x...'],
   *   index: 0,
   *   amount: "150"
   * });
   */
  const claimRewards = useCallback(
    async ({ challengeAddress, proof, index, amount }: ClaimRewardsParams) => {
      try {
        setError(null);

        // Convert amount to proper units
        const amountWei = parseUnits(amount, 6);

        const data = encodeFunctionData({
          abi: DasherABI,
          functionName: 'claimYourStake',
          args: [
            proof as `0x${string}`[],
            BigInt(index),
            amountWei,
          ],
        });

        const result = await sendTransaction({
          to: challengeAddress as `0x${string}`,
          data,
          value: '0x0',
        });

        return result;
      } catch (err: any) {
        console.error('[Contract] Claim rewards failed:', err);
        setError(err);
        throw err;
      }
    },
    [sendTransaction]
  );

  /**
   * Direct USDC transfer (for off-chain/marathon challenge joins)
   *
   * @example
   * const { transferToWallet } = useContractCalls();
   * await transferToWallet({
   *   recipientAddress: '0x...',
   *   tokenAddress: '0x...',
   *   amount: "25"
   * });
   */
  const transferToWallet = useCallback(
    async ({ recipientAddress, tokenAddress, amount }: TransferToWalletParams) => {
      try {
        setError(null);

        // Convert amount to proper units (assuming 6 decimals for USDC)
        const amountWei = parseUnits(amount, 6);

        // ERC20 ABI for transfer function
        const ERC20_TRANSFER_ABI = [
          {
            name: 'transfer',
            type: 'function',
            stateMutability: 'nonpayable',
            inputs: [
              { name: 'to', type: 'address' },
              { name: 'amount', type: 'uint256' },
            ],
            outputs: [{ name: '', type: 'bool' }],
          },
        ] as const;

        // Encode transfer call
        const transferData = encodeFunctionData({
          abi: ERC20_TRANSFER_ABI,
          functionName: 'transfer',
          args: [recipientAddress as `0x${string}`, amountWei],
        });

        // Single transaction - just transfer
        const result = await sendTransaction({
          to: tokenAddress as `0x${string}`,
          data: transferData,
          value: '0x0',
        });

        return result;
      } catch (err: any) {
        console.error('[Contract] Transfer to wallet failed:', err);
        setError(err);
        throw err;
      }
    },
    [sendTransaction]
  );

  /**
   * Leave a challenge (withdraw stake before it starts)
   *
   * Note: This might not be implemented in your contract yet
   */
  const leaveChallenge = useCallback(
    async (_challengeAddress: string) => {
      try {
        setError(null);

        // This would call a withdraw/leave function if it exists
        // For now, throwing error as it's not in the ABI
        throw new Error('Leave challenge not implemented in contract');

        // When implemented:
        // const data = encodeFunctionData({
        //   abi: DasherABI,
        //   functionName: 'leaveChallenge',
        //   args: [],
        // });
        //
        // const result = await sendTransaction({
        //   to: challengeAddress as `0x${string}`,
        //   data,
        // });
        //
        // return result;
      } catch (err: any) {
        console.error('[Contract] Leave challenge failed:', err);
        setError(err);
        throw err;
      }
    },
    [sendTransaction]
  );

  return {
    // Actions
    createChallenge,
    joinChallenge,
    claimRewards,
    leaveChallenge,
    transferToWallet,

    // State
    isLoading,
    error,
  };
}
